import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { apiErrorMessage } from "../services/api";
import { fetchDuplicates, mergeClients } from "../services/clientService";
import { formatDate } from "../services/datetime";

/**
 * BIZ-16's merge — the commonest mess in any CRM.
 *
 * The same person walks in twice, gives a different number the second time, and
 * ends up with two histories. This puts them back together.
 *
 * ## What the screen has to be honest about
 *
 * The client whose profile this is **survives**; the one picked here is folded
 * into it and soft-deleted. A merge only ever *fills blanks* — the survivor's
 * own name, number and email are never overwritten — so the confirmation says
 * exactly that rather than leaving somebody to guess which record wins.
 *
 * The candidate list is deliberately conservative: the API matches on an exact
 * phone, email or name and nothing fuzzier. A near-match suggested wrongly is a
 * merge somebody might accept, and a wrong merge is only unpicked by hand.
 */
export default function ClientMergeModal({ client, open, onClose, onMerged }) {
  const { t, i18n } = useTranslation();

  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [chosen, setChosen] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !client) return;

    setChosen(null);
    setConfirming(false);
    setError("");
    setLoading(true);

    fetchDuplicates(client.id)
      .then(setCandidates)
      .catch((err) => {
        setCandidates([]);
        setError(apiErrorMessage(err, t("clients.merge.loadFailed")));
      })
      .finally(() => setLoading(false));
  }, [open, client, t]);

  if (!open || !client) return null;

  const merge = async () => {
    setBusy(true);
    setError("");
    try {
      const survivor = await mergeClients(client.id, chosen.id);
      onMerged(survivor, chosen);
      onClose();
    } catch (err) {
      setError(apiErrorMessage(err, t("clients.merge.failed")));
      setConfirming(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="card my-8 w-full max-w-2xl p-5">
        <h2 className="mb-1 text-lg font-bold">{t("clients.merge.title")}</h2>
        <p className="mb-4 text-sm text-ink-2">
          {t("clients.merge.subtitle", { name: client.name })}
        </p>

        {loading ? (
          <p className="py-8 text-center text-sm text-ink-2">{t("common.loading")}</p>
        ) : candidates.length === 0 ? (
          <p className="rounded-lg border border-line px-4 py-8 text-center text-sm text-ink-2">
            {t("clients.merge.none")}
          </p>
        ) : (
          <ul className="divide-y divide-line rounded-lg border border-line">
            {candidates.map((candidate) => (
              <li key={candidate.id}>
                <button
                  type="button"
                  onClick={() => setChosen(candidate)}
                  className={`flex w-full flex-wrap items-center justify-between gap-3 px-4 py-3 text-start ${
                    chosen?.id === candidate.id ? "bg-accent-wash" : "hover:bg-raised"
                  }`}
                >
                  <span>
                    <span className="block text-sm font-medium">{candidate.name}</span>
                    <span className="block text-xs text-ink-2" dir="ltr">
                      {[candidate.phone_national, candidate.email].filter(Boolean).join(" · ") ||
                        t("common.none")}
                    </span>
                  </span>

                  <span className="text-xs text-ink-2">
                    {t("clients.merge.candidateStats", {
                      visits: candidate.appointments_count ?? 0,
                      created: formatDate(candidate.created_at, i18n.language),
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {/* Said plainly, because it is the part people get wrong. */}
        {chosen && !confirming && (
          <div className="mt-4 rounded-lg border border-warning-line bg-warning-wash px-3 py-2 text-sm text-warning-text">
            {t("clients.merge.explain", { winner: client.name, loser: chosen.name })}
          </div>
        )}

        {confirming && (
          <div className="mt-4 rounded-lg border border-danger-line bg-danger-wash px-3 py-2 text-sm text-danger-text">
            {t("clients.merge.confirm", { winner: client.name, loser: chosen.name })}
          </div>
        )}

        {error && <p className="field-error mt-3">{error}</p>}

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>

          {confirming ? (
            <button
              type="button"
              onClick={merge}
              disabled={busy}
              className="rounded-lg bg-danger px-4 py-2 text-dense font-semibold text-danger-on transition
                hover:bg-danger-text disabled:cursor-not-allowed disabled:border disabled:border-line-strong disabled:bg-raised disabled:text-ink-2"
            >
              {busy ? t("clients.merge.merging") : t("clients.merge.confirmButton")}
            </button>
          ) : (
            <button
              type="button"
              className="btn-primary"
              disabled={!chosen}
              onClick={() => setConfirming(true)}
            >
              {t("clients.merge.action")}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
